import {HttpService} from "../http/Http.service";
import {message} from "antd";
import {SimulatorPersonIdPair, StatusCodeE} from "../../model/models";
import {HttpErrorService} from "../http/HttpError.service";

export class TemplateUtils {

    static generateDocument(simulatorId: any, personId: number) {
        HttpService.getInstanceAxios().get(`/template/process/simulator/${simulatorId}/person/${personId}/all`, {
            responseType: "blob",
        })
            .then(response => {
                const blob = new Blob([response.data], {type: "application/pdf"});
                const url = URL.createObjectURL(blob);
                window.open(url)
            })

    };

    static async downloadGlyphedDocument(simulatorId: any, personId: number, onComplete?: () => void, status?: StatusCodeE | null) {
        try {
            const blob = await this.fetchGlyphedOrFallbackPDFBlob(simulatorId, personId, status);
            if (!blob) return;

            const url = URL.createObjectURL(new Blob([blob], {type: "application/pdf"}));
            window.open(url);
        } finally {
            if (onComplete) onComplete();
        }
    }

    static async fetchGlyphedOrFallbackPDFBlob(simulatorId: any, personId: number, status?: StatusCodeE | null): Promise<Blob | null> {
        const isValidPDF = async (blob: Blob): Promise<boolean> => {
            if (!blob || blob.size === 0) {
                return false;
            }
            try {
                const text = await blob.slice(0, 4).text();
                return text.startsWith('%PDF');
            } catch {
                return false;
            }
        };

        const fetchPDF = async (url: string): Promise<Blob> => {
            const response = await HttpService.getInstanceAxios().get(url, {
                responseType: "blob",
            });
            return response.data;
        };

        if (status && status === StatusCodeE.DA_FIRMARE) {
            try {
                const fallbackUrl = `/template/process/simulator/${simulatorId}/person/${personId}/all`;
                const fallbackBlob = await fetchPDF(fallbackUrl);

                if (await isValidPDF(fallbackBlob)) {
                    return fallbackBlob;
                } else {
                    throw new Error("PDF da firmare non valido");
                }
            } catch (error) {
                HttpErrorService.handle("Errore durante il download del documento da firmare.", error, true);
                return null;
            }
        }

        try {
            const glyphUrl = `/template/process/simulator/${simulatorId}/person/${personId}/glyph`;
            const glyphedBlob = await fetchPDF(glyphUrl);

            if (await isValidPDF(glyphedBlob)) {
                return glyphedBlob;
            } else {
                throw new Error("PDF con glifo non valido");
            }

        } catch (glyphError) {
            await HttpErrorService.handleBlob("Il servizio di protocollazione non funziona. Contattare il fornitore o riprovare più tardi", glyphError, true);

            try {
                const fallbackUrl = `/template/process/simulator/${simulatorId}/person/${personId}/all`;
                const fallbackBlob = await fetchPDF(fallbackUrl);

                if (await isValidPDF(fallbackBlob)) {
                    return fallbackBlob;
                } else {
                    throw new Error("PDF senza glifo non valido");
                }

            } catch (error) {
                HttpErrorService.handle("Errore durante il download del documento senza glifo.", error, true);
                return null;
            }
        }
    }

    static async previewDocument(uuid: string, originalName: string, personId: number, simulatorId: number) {
        try {
            const response = await HttpService.getInstanceAxios().get(
                `/person/${personId}/document/simulator/${simulatorId}/download/${uuid}`,
                {responseType: 'blob'}
            );

            const contentType = response.headers['content-type'] || 'application/pdf';
            const blob = new Blob([response.data], {type: contentType});
            const blobUrl = URL.createObjectURL(blob);

            const isP7M = originalName.toLowerCase().endsWith(".p7m");

            if (isP7M) {
                const newName = originalName.replace(/\.p7m$/i, ".pdf");

                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = newName;
                a.click();
            } else {
                window.open(blobUrl, "_blank");
            }

        } catch (error) {
            HttpErrorService.handle("Errore durante l'apertura del documento", error, true)
        }
    }


    static downloadSignedDocument(simulatorId: any, personId: number) {
        HttpService.getInstanceAxios().get(`/signer/download/simulator/${simulatorId}/person/${personId}`, {
            responseType: "blob",
        })
            .then(response => {
                const blob = new Blob([response.data], {type: "application/pdf"});
                const url = URL.createObjectURL(blob);

                const contentDisposition = response.headers['content-disposition'];
                let fileName = "document.pdf";

                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (fileNameMatch?.[1]) {
                        fileName = fileNameMatch[1];
                    }
                }

                var a = window.document.createElement("a");
                a.href = url
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            })

    };

    static downloadSignedDocumentsAsZip(simulatorId: any, personIds: number[], onSuccess?: () => void) {
        HttpService.getInstanceAxios().post(`/signer/download/simulator/${simulatorId}/zip`, personIds, {
            responseType: "blob"
        }).then(response => {
            const blob = new Blob([response.data], {type: "application/zip"});
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "documenti.zip";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            if (onSuccess) {
                onSuccess();
            }
        }).catch(() => {
            message.error("Errore durante il download");
        });
    }

    static async downloadDocumentsAsZip(pairs: SimulatorPersonIdPair[], applyStatus: boolean = true, signedBlob: Blob | undefined, onSuccess?: () => void) {
        try {
            const formData = new FormData();

            if (signedBlob)
                formData.append("signedFile", signedBlob);

            formData.append("simulatorPersonIdPairs", JSON.stringify(pairs));
            formData.append("applyStatus", String(applyStatus));

            const data = await HttpService.getInstanceAxios().post(`/signer/download/scadute-user-zip`, formData, {
                responseType: "blob"
            })

            // This part of the code is already correct
            const blob = new Blob([data.data], {type: "application/zip"});
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "documenti.zip";
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 0);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            HttpErrorService.handle("Errore durante il download", error, true);
        }
    }
}